import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { getFieldHelp } from "./registry";

/**
 * Hover card explaining a field: what it is for, and where its value comes
 * from (measured, calculated, a default, a user setting, or user input).
 *
 *   <FieldHelp id="live.totalLoad">Total Load</FieldHelp>
 *
 * Wraps its children in place, so labels keep their own styling; the dotted
 * underline is the only visual cue. Text lives in src/help/<tab>.js, keyed by
 * id, so wording can be reviewed in one place instead of across components.
 * Pass `help` instead of `id` for a one-off entry built at render time.
 *
 * The card waits SHOW_DELAY_MS before opening so that moving the mouse across
 * the dashboard, or clicking a labelled control, doesn't pop cards up.
 */

export const SOURCE_META = {
  measured: { label: "From the breaker", color: "#0e7490", bg: "#ecfeff" },
  calculated: { label: "Calculated", color: "#6d28d9", bg: "#f5f3ff" },
  backend: { label: "From the system", color: "#1d4ed8", bg: "#eff6ff" },
  default: { label: "Default", color: "#b45309", bg: "#fffbeb" },
  setting: { label: "Your setting", color: "#047857", bg: "#ecfdf5" },
  input: { label: "You enter", color: "#be185d", bg: "#fdf2f8" },
};

const CARD_WIDTH = 320;
const GAP = 8;
const SHOW_DELAY_MS = 2000;
const FADE_MS = 320;
const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
const warned = new Set();

// Focus from a click shouldn't open the card, only keyboard focus should.
const isKeyboardFocus = (el) => {
  try {
    return el.matches(":focus-visible");
  } catch {
    return true;
  }
};

// A wrapper set to display:contents has no box of its own, so fall back to the
// area its children cover. That lets a whole group of buttons share one card
// without an extra element changing the layout.
const anchorRect = (el) => {
  const rect = el.getBoundingClientRect();
  if (rect.width || rect.height) return rect;
  const boxes = Array.from(el.children).map((c) => c.getBoundingClientRect());
  if (!boxes.length) return rect;
  const left = Math.min(...boxes.map((b) => b.left));
  const right = Math.max(...boxes.map((b) => b.right));
  const top = Math.min(...boxes.map((b) => b.top));
  const bottom = Math.max(...boxes.map((b) => b.bottom));
  return { left, right, top, bottom, width: right - left, height: bottom - top };
};

const FieldHelp = ({ id, help, children, inline = true, style }) => {
  const entry = help || getFieldHelp(id);
  const anchorRef = useRef(null);
  const cardRef = useRef(null);
  const [open, setOpen] = useState(false);
  // A card opened by a click stays put until the next click; a card opened by
  // hovering follows the pointer away.
  const [pinned, setPinned] = useState(false);
  const [pos, setPos] = useState(null);

  if (!entry && id && process.env.NODE_ENV === "development" && !warned.has(id)) {
    warned.add(id);
    // eslint-disable-next-line no-console
    console.warn(`[FieldHelp] no help text for "${id}"`);
  }

  const timerRef = useRef(null);

  const cancel = useCallback(() => {
    clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);
  const scheduleShow = useCallback(() => {
    if (pinned) return;
    cancel();
    timerRef.current = setTimeout(() => setOpen(true), SHOW_DELAY_MS);
  }, [cancel, pinned]);
  const close = useCallback(() => {
    cancel();
    setPinned(false);
    setOpen(false);
    setPos(null);
  }, [cancel]);
  const hide = useCallback(() => {
    cancel();
    if (pinned) return;
    setOpen(false);
    setPos(null);
  }, [cancel, pinned]);
  // The cursor says what a click will do. Where it shows the help arrow the
  // click opens this card, and closes it again next time. Over a button or an
  // input the click belongs to that control, so the card just gets out of the
  // way.
  const onClick = useCallback(
    (e) => {
      const onControl = e.target.closest?.(
        "button, a, select, input, textarea, label, [role='button'], [role='tab']",
      );
      if (onControl || open || pinned) {
        close();
        return;
      }
      cancel();
      setPinned(true);
      setOpen(true);
    },
    [cancel, close, open, pinned],
  );
  const onFocus = useCallback(
    (e) => {
      if (isKeyboardFocus(e.currentTarget)) scheduleShow();
    },
    [scheduleShow],
  );

  useEffect(() => cancel, [cancel]);

  // The card is fixed to where the label was; close it once the page moves.
  useEffect(() => {
    if (!open) return undefined;
    window.addEventListener("scroll", close, true);
    return () => window.removeEventListener("scroll", close, true);
  }, [open, close]);

  useEffect(() => {
    if (!pinned) return undefined;
    const onElsewhere = (e) => {
      if (!anchorRef.current?.contains(e.target)) close();
    };
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("mousedown", onElsewhere, true);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("mousedown", onElsewhere, true);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [pinned, close]);

  // Place below the label, flipping above / shifting sideways to stay on screen.
  useLayoutEffect(() => {
    if (!open || !anchorRef.current || !cardRef.current) return;
    const a = anchorRect(anchorRef.current);
    const c = cardRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let top = a.bottom + GAP;
    if (top + c.height > vh - GAP && a.top - GAP - c.height > GAP) {
      top = a.top - GAP - c.height;
    }
    let left = a.left + a.width / 2 - c.width / 2;
    left = Math.max(GAP, Math.min(left, vw - c.width - GAP));
    setPos({ top, left });
  }, [open]);

  if (!entry) return <>{children}</>;

  const Tag = inline ? "span" : "div";
  const source = SOURCE_META[entry.source];

  return (
    <Tag
      ref={anchorRef}
      tabIndex={0}
      aria-label={entry.title || undefined}
      onMouseEnter={scheduleShow}
      onMouseLeave={hide}
      onClick={onClick}
      onFocus={onFocus}
      onBlur={hide}
      style={{ cursor: "help", outline: "none", ...style }}
    >
      {children}
      {open &&
        createPortal(
          <div
            ref={cardRef}
            role="tooltip"
            style={{
              ...styles.card,
              top: pos?.top ?? -9999,
              left: pos?.left ?? -9999,
              // Measured while invisible, then eased in from just off the
              // label once placed; the measuring pass fixes the start state.
              opacity: pos ? 1 : 0,
              transform: pos ? "none" : "translateY(6px)",
              transition: prefersReducedMotion()
                ? "none"
                : `opacity ${FADE_MS}ms ease-out, transform ${FADE_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`,
            }}
          >
            <div style={styles.headerRow}>
              {entry.title && <span style={styles.title}>{entry.title}</span>}
              {source && (
                <span style={{ ...styles.badge, color: source.color, background: source.bg }}>
                  {source.label}
                </span>
              )}
            </div>
            {entry.use && <p style={styles.text}>{entry.use}</p>}
            {entry.how && (
              <>
                <div style={styles.subhead}>Working</div>
                <p style={styles.text}>{entry.how}</p>
              </>
            )}
          </div>,
          document.body,
        )}
    </Tag>
  );
};

const styles = {
  card: {
    position: "fixed",
    zIndex: 100000,
    width: CARD_WIDTH,
    maxWidth: "calc(100vw - 16px)",
    background: "#fff",
    color: "#0f172a",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    boxShadow: "0 10px 25px rgba(15,23,42,0.15)",
    padding: "10px 12px",
    fontSize: 12,
    fontWeight: 400,
    lineHeight: 1.45,
    textAlign: "left",
    textTransform: "none",
    letterSpacing: "normal",
    whiteSpace: "normal",
    pointerEvents: "none",
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    marginBottom: 4,
  },
  title: { fontWeight: 600, fontSize: 13 },
  badge: {
    fontSize: 10,
    fontWeight: 600,
    padding: "2px 6px",
    borderRadius: 999,
    whiteSpace: "nowrap",
  },
  subhead: {
    marginTop: 6,
    fontSize: 10,
    fontWeight: 600,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  text: { margin: "2px 0 0", color: "#334155" }
};

export default FieldHelp;
